import { useState } from "react";
import { useTranslation } from "react-i18next";
import WebApp from "@twa-dev/sdk";
import type { ColorVariant, SelectionResult } from "../../types";
import { AppHeader, Banner, SectionHead, Icon } from "../common/ui";
import PigmentJar from "./PigmentJar";

/** Build the client-facing aftercare message (the only part shared with clients). */
export function buildAftercareText(
  result: SelectionResult,
  clientName: string | null | undefined,
  header: string,
): string {
  const lines: string[] = [];
  lines.push(clientName ? `${header} — ${clientName}` : header);
  lines.push("");
  for (const c of result.care_recommendations) lines.push(`• ${c}`);
  return lines.join("\n");
}

/** Open Telegram's native "Share to…" chooser with the given text. */
function shareToTelegram(text: string): boolean {
  const url = `https://t.me/share/url?url=&text=${encodeURIComponent(text)}`;
  try {
    if (typeof WebApp?.openTelegramLink === "function") {
      WebApp.openTelegramLink(url);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    window.open(url, "_blank");
    return true;
  } catch {
    return false;
  }
}

/** Collapsed by default — the master reads the recipe, not the theory. */
function Fold({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="glass glass-lg" style={{ padding: open ? 20 : 14 }}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="row between"
        style={{
          width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer",
          color: "inherit", font: "inherit", textAlign: "left",
        }}
      >
        <span className="row" style={{ gap: 9, minWidth: 0 }}>
          <span style={{ color: "var(--accent-text)", flex: "none", display: "inline-flex" }}>
            <Icon name={icon} size={17} />
          </span>
          <span className="t-h">{title}</span>
        </span>
        <span style={{
          flex: "none", color: "var(--text-2)", display: "inline-flex",
          transform: open ? "rotate(90deg)" : "none", transition: "transform 0.18s ease",
        }}>
          <Icon name="chevronRight" size={18} />
        </span>
      </button>
      {open && <div style={{ marginTop: 14 }}>{children}</div>}
    </section>
  );
}

/**
 * The recipe card. Jars with their drop counts carry the recipe; beside them a
 * short ingredient list (role and share) and one proportion bar in the real
 * pigment colours, so the balance of the mix reads before any number does.
 * `step` numbers the card when the procedure has more than one mix (cover-up
 * first, then the working mix) — the order is the point there.
 */
export function MixCard({ v, badge, accent, step }: { v: ColorVariant; badge?: string; accent?: boolean; step?: number }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const jars = v.components ?? [];
  const total = v.total_drops || jars.reduce((n, c) => n + c.drops, 0);

  const copy = () => {
    const text = jars.length
      ? `${v.name}: ${jars.map((c) => `${c.short} — ${c.drops} кап.`).join(" + ")}`
      : `${v.name}: ${v.mix}`;
    // Telegram's in-app WebView does not always expose the clipboard.
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      },
      () => {},
    );
  };

  return (
    <section className={"mix-card" + (accent ? " is-accent" : "")}>
      <header className="mix-card__head">
        {step ? (
          <span className="mix-card__step" aria-hidden>{step}</span>
        ) : (
          <span className="mix-card__swatch" style={{ background: v.predicted_color }} aria-hidden />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          {badge && <div className="mix-card__eyebrow">{badge}</div>}
          <h3 className="mix-card__name">{v.name}</h3>
        </div>
        <button className="mix-card__copy" onClick={copy} aria-label={t("selection.copyMix")} title={t("selection.copyMix")}>
          <Icon name={copied ? "check" : "copy"} size={16} />
          <span>{copied ? t("selection.copied") : t("selection.copyShort")}</span>
        </button>
      </header>

      {jars.length > 0 ? (
        <div className="mix-card__body">
          <div className="mix-card__jars">
            {jars.map((c, i) => (
              <div key={c.shade_id + i} className="row" style={{ gap: 6 }}>
                {i > 0 && <span className="mix-card__plus" aria-hidden>+</span>}
                <PigmentJar c={c} size={72} />
              </div>
            ))}
          </div>

          <div className="mix-card__facts">
            <ul className="mix-card__list">
              {jars.map((c, i) => (
                <li key={c.shade_id + i}>
                  <span className="mix-card__dot" style={{ background: c.hex }} aria-hidden />
                  <span className="mix-card__shade">{c.name}</span>
                  {c.role && <span className="mix-card__role">{c.role}</span>}
                  <span className="mix-card__pct">{total ? Math.round((c.drops / total) * 100) : 0}%</span>
                </li>
              ))}
            </ul>
            <div className="mix-card__bar" role="img"
              aria-label={jars.map((c) => `${c.name} ${c.drops} ${t("selection.dropsShort")}`).join(", ")}>
              {jars.map((c, i) => (
                <span key={c.shade_id + i} style={{ flexGrow: c.drops, background: c.hex }} />
              ))}
            </div>
            <div className="mix-card__total">
              <span>{t("selection.totalDrops")} <strong>{total} {t("selection.dropsShort")}</strong></span>
              {v.ratio && <span>{t("selection.ratio")} <span className="t-mono">{v.ratio}</span></span>}
            </div>
          </div>
        </div>
      ) : (
        // Sessions saved before the drop-based engine keep only the text recipe.
        <p className="t-sm t-mono muted" style={{ margin: "14px 0 0", lineHeight: 1.5 }}>{v.mix}</p>
      )}

      {v.note && <p className="mix-card__note">{v.note}</p>}
    </section>
  );
}

/** Jump to a section of the report from the summary tiles. */
function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function ResultScreen({
  result, onExit, theme, onThemeChange, clientName, createdAt, onRestart, onOpenLesson, onOpenPlans, onHome, calcNumber,
}: {
  /** «Расчёт №1234»; falls back to result.number (set by /recommend). */
  calcNumber?: number | null;
  result: SelectionResult;
  onExit: () => void;
  /** Leave the whole flow for the main menu (the header back only steps back). */
  onHome?: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
  clientName?: string | null;
  createdAt?: string | null;
  onRestart?: () => void;
  onOpenLesson?: (lessonId: string) => void;
  onOpenPlans?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const canShare = result.care_recommendations.length > 0;

  const variants = result.color_variants ?? [];
  const covers = variants.filter((v) => v.kind === "coverup");
  const mixes = variants.filter((v) => v.kind !== "coverup");
  const [primary, ...alts] = mixes;

  const onShare = () => {
    const text = buildAftercareText(result, clientName, t("selection.shareHeader"));
    const ok = shareToTelegram(text);
    if (!ok && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const dateStr = createdAt
    ? new Date(createdAt).toLocaleDateString(i18n.language?.startsWith("en") ? "en-GB" : "ru-RU", {
        day: "2-digit", month: "long", year: "numeric",
      })
    : null;

  const pigmentLabel = result.pigment_type === "mineral" ? t("selection.pigmentMineral")
    : result.pigment_type === "hybrid" ? t("selection.pigmentHybrid") : null;
  const subtitleParts = [
    dateStr,
    pigmentLabel,
  ].filter(Boolean);

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("selection.resultTitle")} />
      <div className="wrap" style={{ paddingTop: 6 }}>
        {/* Desktop: the working report in a centred main column, the folded
            extras and the way out beside it. Phone: one column, in that order. */}
        <div className="result-page">
        {/* Summary: who, when, and the three answers the master needs mid-
            procedure. Each tile jumps to its full section below. */}
        <header className="result-hero anim-rise">
          <div className="result-hero__id">
            <span className="result-hero__icon"><Icon name="sparkles" size={24} /></span>
            <div style={{ minWidth: 0 }}>
              <p className="eyebrow" style={{ margin: 0 }}>{t("selection.resultTitle")}</p>
              <h1 className="result-hero__name">{clientName || t("selection.resultUntitled")}</h1>
              {subtitleParts.length > 0 && <p className="t-sm muted" style={{ margin: 0 }}>{subtitleParts.join(" · ")}</p>}
              {(calcNumber ?? result.number) != null && (
                <p className="t-xs muted-2" style={{ margin: "2px 0 0" }} title={t("selection.calcNumberHint")}>
                  {t("selection.calcNumber", { number: calcNumber ?? result.number })}
                </p>
              )}
            </div>
          </div>
          <div className="result-tiles">
            {primary && (
              <button className="result-tile" onClick={() => scrollToId("res-mix")}>
                <span className="result-tile__label"><Icon name="droplet" size={14} /> {t("selection.tileMix")}</span>
                <span className="result-tile__value">
                  <span className="result-tile__swatch" style={{ background: primary.predicted_color }} />
                  {primary.total_drops ? `${primary.total_drops} ${t("selection.dropsShort")}` : primary.name}
                  {primary.ratio && <span className="result-tile__sub t-mono">{primary.ratio}</span>}
                </span>
              </button>
            )}
            {result.cartridge && (
              <button className="result-tile" onClick={() => scrollToId("res-cartridge")}>
                <span className="result-tile__label"><Icon name="brush" size={14} /> {t("selection.cartridge")}</span>
                <span className="result-tile__value t-mono">{result.cartridge.label.split(" (")[0]}</span>
              </button>
            )}
            {result.layup_title || result.layup.length > 0 ? (
              <button className="result-tile" onClick={() => scrollToId("res-layup")}>
                <span className="result-tile__label"><Icon name="layers" size={14} /> {t("selection.tileScheme")}</span>
                <span className="result-tile__value">{result.layup_title || t("selection.layupScheme")}</span>
              </button>
            ) : (
              <div className="result-tile is-static">
                <span className="result-tile__label"><Icon name="layers" size={14} /> {t("selection.tileScheme")}</span>
                <span className="result-tile__value muted">{t("selection.tileSchemeSkipped")}</span>
              </div>
            )}
          </div>
        </header>

        {/* Desktop: the working report in a centred main column, the folded
            extras and the way out beside it. Phone: one column, in that order. */}
        <div className="result-layout">
        <div className="result-main">
          {(result.applied_rules ?? []).map((rule) => (
            <div key={rule.rule_id} style={{ marginBottom: 10 }}>
              <Banner kind="success">
                <strong>{t("selection.ruleApplied")}: {rule.rule_id} · {rule.title}</strong><br />
                {rule.explanation}
              </Banner>
            </div>
          ))}
          {(result.warnings ?? []).map((warning, index) => (
            <div key={index} style={{ marginBottom: 10 }}><Banner kind="error">{warning}</Banner></div>
          ))}

          {/* ── Final report: the working mix chosen on the comparison step.
              With old PMU the cover-up goes first, so the cards become steps. ── */}
          <div id="res-mix" className="result-anchor">
            <SectionHead icon="droplet">{t("selection.yourMix")}</SectionHead>
          </div>
          <div className="mix-grid" style={{ marginBottom: 18 }}>
            {covers.map((v, i) => (
              <MixCard key={`c${i}`} v={v} step={i + 1} badge={t("selection.stepCoverup")} />
            ))}
            {primary && (
              <MixCard v={primary} accent step={covers.length ? covers.length + 1 : undefined}
                badge={covers.length ? t("selection.stepWorkingMix") : t("selection.badgeMain")} />
            )}
            {alts.map((v, i) => (
              <MixCard key={`a${i}`} v={v} badge={t("selection.badgeAlt")} />
            ))}
          </div>

          {result.finish_pass && (
            <section className="glass glass-lg" style={{ padding: 20, marginBottom: 18 }}>
              <SectionHead icon="sparkles">{t("selection.finishPass")}</SectionHead>
              <div className="glass glass-1" style={{ padding: 16, borderRadius: 16 }}>
                <div className="row" style={{ gap: 14, alignItems: "center" }}>
                  {result.finish_pass.jar ? (
                    <img src={result.finish_pass.jar} alt={result.finish_pass.shade_name}
                      style={{ width: 58, height: 74, objectFit: "contain", flex: "none", filter: "drop-shadow(0 5px 9px rgba(0,0,0,.28))" }} />
                  ) : (
                    <span style={{ width: 48, height: 48, borderRadius: 13, flex: "none", background: result.finish_pass.shade_hex, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.24)" }} />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <span className="badge badge-violet">Mineral · {t("selection.purePigment")}</span>
                    <div className="t-h" style={{ marginTop: 7 }}>{result.finish_pass.title}</div>
                    <div className="t-mono" style={{ marginTop: 4, color: "var(--accent-text)", fontWeight: 800 }}>
                      {result.finish_pass.shade_name}
                    </div>
                  </div>
                </div>
                <p className="t-sm" style={{ marginTop: 12, fontWeight: 700, lineHeight: 1.5 }}>{result.finish_pass.instruction}</p>
                <p className="t-xs muted" style={{ marginTop: 7, lineHeight: 1.5 }}>{result.finish_pass.reason}</p>
              </div>
            </section>
          )}

            <div className="stack" style={{ "--gap": "18px" } as React.CSSProperties}>
              {result.cartridge && (
                <section id="res-cartridge" className="glass glass-lg result-anchor" style={{ padding: 20 }}>
                  <SectionHead icon="brush">{t("selection.cartridge")}</SectionHead>
                  <div className="glass-1 glass" style={{ padding: 16, borderRadius: 16 }}>
                    <div className="t-mono" style={{ fontSize: "1.15rem", fontWeight: 700, color: "var(--accent-text)", letterSpacing: "0.02em" }}>
                      {result.cartridge.label}
                    </div>
                    <p className="t-sm muted" style={{ marginTop: 6, lineHeight: 1.45 }}>{result.cartridge.reason}</p>
                  </div>
                </section>
              )}

              {(result.layup_title || result.layup.length > 0) && <section id="res-layup" className="glass glass-lg result-anchor" style={{ padding: 20 }}>
                <SectionHead icon="layers">{t("selection.layupScheme")}</SectionHead>
                {result.layup_title && (
                  <p className="t-body" style={{ fontWeight: 800, margin: "0 0 10px" }}>{result.layup_title}</p>
                )}

                {/* Link into the lesson that teaches this scheme. Locked without a
                    subscription -> send the master to the plans instead. */}
                {result.layup_lesson_id && (onOpenLesson || onOpenPlans) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (result.layup_lesson_locked) onOpenPlans?.();
                      else onOpenLesson?.(result.layup_lesson_id as string);
                    }}
                    className="glass glass-1"
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%",
                      padding: "11px 13px", marginBottom: 12, borderRadius: 13,
                      border: "1px solid var(--border)", cursor: "pointer",
                      color: "inherit", textAlign: "left",
                    }}
                  >
                    <span style={{
                      display: "grid", placeItems: "center", width: 30, height: 30, borderRadius: 9,
                      flex: "none", background: "var(--violet-tint)", color: "var(--violet-text)",
                    }}>
                      <Icon name={result.layup_lesson_locked ? "lock" : "graduation"} size={16} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="t-xs muted-2" style={{ display: "block" }}>
                        {result.layup_lesson_locked ? t("selection.schemeLessonLocked") : t("selection.schemeLessonLabel")}
                      </span>
                      <span className="t-sm" style={{ fontWeight: 700, color: "var(--accent-text)" }}>
                        {result.layup_lesson_title || result.layup_title}
                      </span>
                    </span>
                    <Icon name="chevronRight" size={18} />
                  </button>
                )}

                {result.layup_style && (
                  <span className="badge badge-violet" style={{
                    marginBottom: 12, whiteSpace: "normal", height: "auto",
                    padding: "0.35rem 0.7rem", lineHeight: 1.45, textAlign: "left",
                    alignItems: "flex-start",
                  }}>
                    <span style={{ flex: "none", marginTop: 2, display: "inline-flex" }}><Icon name="sparkles" size={12} /></span>
                    <span>{t("selection.layupStyle")}: {result.layup_style}</span>
                  </span>
                )}
                {result.layup.length === 0 && (
                  <p className="t-sm muted" style={{ margin: "10px 0 0", lineHeight: 1.45 }}>
                    {t("selection.layupComing")}
                  </p>
                )}
                <ol className="stack" style={{ "--gap": "8px", listStyle: "none", margin: "12px 0 0", padding: 0 } as React.CSSProperties}>
                  {result.layup.map((s) => (
                    <li key={s.pass_number} className="glass-1 glass" style={{ padding: 13, borderRadius: 14, display: "flex", gap: 12 }}>
                      <span style={{
                        width: 28, height: 28, borderRadius: 9, flex: "none", display: "grid", placeItems: "center",
                        background: "var(--accent-tint)", color: "var(--accent-text)", fontWeight: 800, fontSize: "0.85rem",
                      }}>{s.pass_number}</span>
                      <div style={{ minWidth: 0 }}>
                        <div className="t-xs" style={{ color: "var(--accent-text)", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                          {s.direction}
                        </div>
                        <div className="t-sm" style={{ fontWeight: 600, marginTop: 2, lineHeight: 1.4 }}>{s.instruction}</div>
                      </div>
                    </li>
                  ))}
                </ol>
                {result.video_url && (
                  <a href={result.video_url} target="_blank" rel="noreferrer"
                    className="btn btn-glass btn-block" style={{ marginTop: 14, textDecoration: "none" }}>
                    <Icon name="play" size={18} /> {t("selection.watchVideo")}
                  </a>
                )}
              </section>}
            </div>
        </div>

          <aside className="result-side">
            {/* Everything the master does not need mid-procedure lives folded. */}
            <div className="stack" style={{ "--gap": "18px" } as React.CSSProperties}>
              <Fold icon="heart" title={t("selection.careTitle")}>
                <ul className="stack" style={{ "--gap": "9px", listStyle: "none", margin: 0, padding: 0 } as React.CSSProperties}>
                  {result.care_recommendations.map((c, i) => (
                    <li key={i} className="row" style={{ alignItems: "flex-start", gap: 10 }}>
                      <span style={{ color: "var(--success)", marginTop: 1, flex: "none" }}>
                        <Icon name="check" size={17} stroke={2.5} />
                      </span>
                      <span className="t-sm" style={{ fontWeight: 600, lineHeight: 1.45 }}>{c}</span>
                    </li>
                  ))}
                </ul>
                {canShare && (
                  <button className="btn btn-primary btn-block" onClick={onShare} style={{ marginTop: 16 }}>
                    <Icon name="share" size={18} /> {copied ? t("selection.copied") : t("selection.shareClient")}
                  </button>
                )}
              </Fold>

              <Fold icon="info" title={t("selection.whyTitle")}>
                <ul className="stack" style={{ "--gap": "9px", listStyle: "none", margin: 0, padding: 0 } as React.CSSProperties}>
                  {(result.reasoning ?? []).map((r, i) => (
                    <li key={i} className="t-sm muted" style={{ lineHeight: 1.5, display: "flex", gap: 8 }}>
                      <span style={{ color: "var(--accent-text)", flex: "none" }}>•</span>
                      <span style={r.startsWith("⚠️") ? { color: "var(--warn-text, #a05a00)", fontWeight: 600 } : undefined}>{r}</span>
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 14 }}>
                  <Banner kind="info">{result.disclaimer}</Banner>
                </div>
              </Fold>
            </div>

            <div className="result-actions">
              {onRestart && (
                <button className="btn btn-glass btn-block" onClick={onRestart}>
                  <Icon name="rotate" size={18} /> {t("selection.restart")}
                </button>
              )}
              {onHome && (
                <button className="btn btn-primary btn-block" onClick={onHome}>
                  <Icon name="home" size={18} /> {t("selection.toMainMenu")}
                </button>
              )}
            </div>
          </aside>
        </div>
        </div>
      </div>
    </div>
  );
}
